import { last } from "shared/lib/arrayUtils"
import { TreeNode } from "shared/types/TreeNode"
import { WithID } from "shared/types/WithID"
import { DragSource } from "shared/types/drag-and-drop/DragSource"

import { FlattenedTableItemType } from "../types"
import { TableDragMeta } from "../types/TableDragMeta"
import { findParent } from "./findParent"
import { getHeight } from "./getHeight"

function insertAt<T extends WithID>({
  nodes,
  unavailableParentIndex,
  index,
  insertBefore
}: {
  nodes: TreeNode<FlattenedTableItemType<T>>[]
  unavailableParentIndex: number
  index: number
  insertBefore: boolean
}) {
  const unavailableParentHeight = getHeight(nodes, unavailableParentIndex)
  const hasEvenHeight = unavailableParentHeight % 2 === 0

  /**
   *  When the unavailable parent has an even height the node should be inserted after the parent
   *  plus all of its children once the halfway point has been crossed.
   *  For that the target is set to +1.
   *  Otherwise it matters wether the user is dragging over the top or the bottom of the element
   *  If it is the top (insertBefore === true) the target is before the element (+0).
   *  Otherwise it is after (+1).
   */
  const targetIndex = hasEvenHeight ? index + 1 : index + (insertBefore ? 0 : 1)

  const distanceToParent = targetIndex - unavailableParentIndex
  const isOverMiddle = unavailableParentHeight / 2 < distanceToParent

  if (isOverMiddle) {
    return {
      index: unavailableParentIndex + unavailableParentHeight,
      parentID: last(nodes[unavailableParentIndex].parents) || null
    }
  }

  return {
    index: unavailableParentIndex,
    parentID: last(nodes[unavailableParentIndex].parents) || null
  }
}

export function findInsertionIndex<T extends WithID>({
  nodes,
  insertBefore,
  maxDepth,
  index,
  canInsertInto = () => true,
  dragSource
}: {
  nodes: TreeNode<FlattenedTableItemType<T>>[]
  index: number
  insertBefore: boolean
  maxDepth: number
  canInsertInto?: (args: {
    target: TreeNode<FlattenedTableItemType<T>> | null
    source: DragSource<unknown, TableDragMeta>
  }) => boolean
  dragSource: DragSource<unknown, TableDragMeta> | null
}): { index: number; parentID: string | null } {
  function canInsert(parent: TreeNode<FlattenedTableItemType<T>> | null) {
    if (!dragSource) return false
    if (!parent) {
      return canInsertInto({ target: null, source: dragSource })
    }

    return (
      canInsertInto({ target: parent, source: dragSource }) &&
      parent.level + dragSource.meta.depth < maxDepth
    )
  }
  const item = nodes[index]

  if (!dragSource) {
    return { parentID: null, index: -1 }
  }
  if (index >= nodes.length && canInsert(null)) {
    return { parentID: null, index }
  }
  if (!item) {
    return { parentID: null, index: -1 }
  }
  const canInsertAfter = canInsert(item)
  if (canInsertAfter && !insertBefore) {
    return { parentID: item?.id, index: index + 1 }
  }

  let nextSiblingIndex: number = index
  let parent = last(item.parents) || null
  let parentData = findParent(nodes, index, parent)
  let i = item.parents.length - 1

  while (!canInsert(parentData.parent) && i >= 0) {
    i -= 1
    nextSiblingIndex = parentData.index
    if (i !== -1) {
      parent = item.parents[i]
      parentData = findParent(nodes, index, parent)
    } else {
      parent = null
      parentData = { index: -1, parent: null }
    }
  }
  const canInsertOnTopLevel = canInsert(null)
  if (parentData.parent || canInsertOnTopLevel) {
    return insertAt({
      nodes,
      index,
      unavailableParentIndex: nextSiblingIndex,
      insertBefore
    })
  }

  if (!canInsertOnTopLevel && canInsertAfter) {
    return { parentID: item?.id, index: index + 1 }
  }

  // If we can't insert into any parent, including top-level (null), check insert after
  return { parentID: null, index: -1 }
}
